<script setup lang="ts">
import type { Component } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import ElementProvider from '../Elemental/context/ElementProvider.vue'
import ElementalFormContext from './ElementalFormContextProvider/index.vue'
import { useGetElementalQuery } from '~~/src/composables/query/elementals/useGetElementalQuery'
import { useOwnershipDataQuery } from '~~/src/composables/query/elementals/useOwnershipDataQuery'
import type { ElementalFormData } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'

interface Props {
  elementalId?: string
  elementalTypeId: ElementalTypeEnum
  isSidebarView?: boolean
  autosave?: boolean
}

const { isSidebarView = true, ...props } = defineProps<Props>()
const elementalTypeId = toRef(props, 'elementalTypeId')
const elementalId = toRef(props, 'elementalId')

const FormSections: Partial<Record<ElementalTypeEnum, Component>> = {
  [ElementalTypeEnum.List]: defineAsyncComponent(() => import('./ListForm/ListSection/index.vue')),
  [ElementalTypeEnum.Prompt]: defineAsyncComponent(() => import('./PromptForm/PromptSection/index.vue')),
  [ElementalTypeEnum.Snippet]: defineAsyncComponent(() => import('./SnippetForm/SnippetSection/index.vue')),
  [ElementalTypeEnum.Document]: defineAsyncComponent(() => import('./DocumentForm/DocumentSection/index.vue')),
  [ElementalTypeEnum.Automation]: defineAsyncComponent(() => import('./AutomationForm/AutomationSection/index.vue')),
  [ElementalTypeEnum.Table]: defineAsyncComponent(() => import('./TableForm/TableSection/index.vue')),
  [ElementalTypeEnum.Cell]: defineAsyncComponent(() => import('./CellForm/index.vue')),
  [ElementalTypeEnum.Image]: defineAsyncComponent(() => import('./PromptForm/ImageSection/index.vue')),

}

const { data, isLoading, refetch, isFetching, error, isError } = useGetElementalQuery({
  id: elementalId,
  typeRoute: elementalTypeId,
}, {
  enabled: !!elementalId.value,
})
const router = useRouter()
const queryClient = useQueryClient()

const enableAutosave = true

const elementaId = computed(() => data.value?.id || null)

watch(elementaId, (id) => {
  if (id)
    refetch()
})

watch(isError, (isError) => {
  if (isError && (error.value as any)?.status === 404) {
    router.replace('/404').then(() => {
      window.location.reload()
    })
  }
})

onMounted(async () => {
  if (elementalId.value)
    refetch()
})

const elementalTypeData = computed(() => getElementalMetadata(elementalTypeId.value))
const { onSubmit, isPendingCreate, isPendingUpdate } = useOnSubmitElemental(elementalTypeData, isSidebarView, elementaId, data.value?.user.id, { justSave: isSidebarView, goToEdit: elementalTypeId.value === ElementalTypeEnum.Table || enableAutosave })

const submitError = ref(false)
const submitForm = async (values: ElementalFormData) => {
  const result = await onSubmit(values)
  if (!result) {
    submitError.value = true
    return
  }
  submitError.value = false
  if (result?.url && result?.url !== data.value?.url) {
    queryClient.setQueryData(
      ['elemental', elementalTypeId.value, elementalId.value],
      (oldData: any) => ({ ...oldData, url: result.url }),
    )
  }
}

const { data: ownershipData, isLoading: isLoadingOwnership } = useOwnershipDataQuery({
  id: props.elementalId,
}, { enabled: !!props.elementalId })
</script>

<template>
  <ElementProvider
    :elemental="data"
    :type="elementalTypeId"
    :is-loading="isLoadingOwnership"
    :ownership="ownershipData"
  >
    <ElementalFormContext
      class="h-full min-h-fit isolate"
      :autosave="enableAutosave"
      :elemental="data"
      :is-sidebar-view="isSidebarView"
      :elementa-type-id="elementalTypeId"
      :disabled="(isPendingCreate || isPendingUpdate) && !enableAutosave"
      :is-loading="(isLoading || isPendingCreate || isPendingUpdate) && !enableAutosave"
      :is-saving="isPendingCreate || isPendingUpdate"
      :submit-error="submitError"
      @submit="submitForm"
    >
      <div
        v-if="!data && (isLoading || isFetching)" id="loading"
        class="absolute h-full w-full  backdrop-blur-3  z-99 flex flex-col items-center justify-center  "
      >
        <div class="rounded-full flex items-center justify-center gap-2">
          <div class="w-4 h-4 bg-primary rounded-full animate-bounce animate-delay-100" />
          <div class="w-3 h-3 bg-primary/30 rounded-full animate-bounce animate-delay-200" />
          <div class="w-2 h-2 bg-primary/50 rounded-full animate-bounce animate-delay-300" />
        </div>
      </div>
      <div v-else>
        <component
          :is="FormSections[elementalTypeId]"
          v-if="FormSections[elementalTypeId] "
        />
      </div>
    </ElementalFormContext>
  </ElementProvider>
</template>
